import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Clock,
  LayoutDashboard,
  Calendar,
  Users,
  Building2,
  MapPin,
  Palmtree,
  AlertTriangle,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ArrowLeftRight,
  CalendarClock,
  Shield,
  FileSpreadsheet
} from 'lucide-react';
import { NotificationsDropdown } from './NotificationsDropdown';

const navItems = [
  { path: '/super-admin', label: 'Super Admin', icon: Shield, roles: ['super_admin'] },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/clock', label: 'Clock In/Out', icon: Clock, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/schedule', label: 'Schedule', icon: Calendar, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/shift-swaps', label: 'Shift Swaps', icon: ArrowLeftRight, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/availability', label: 'Availability', icon: CalendarClock, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/vacations', label: 'Vacations', icon: Palmtree, roles: ['admin', 'manager', 'supervisor', 'employee'] },
  { path: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/departments', label: 'Departments', icon: Building2, roles: ['admin', 'manager'] },
  { path: '/branches', label: 'Branches', icon: MapPin, roles: ['admin', 'manager'] },
  { path: '/flagged', label: 'Flagged', icon: AlertTriangle, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/reports', label: 'Reports', icon: FileSpreadsheet, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
];

export const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => hasRole(item.roles));

  const getInitials = () => {
    if (!user) return '??';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      manager: 'Manager',
      supervisor: 'Supervisor',
      employee: 'Employee'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile menu overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-card border-r overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b">
            <img src="/logo.png" alt="TeamTime" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold font-['Manrope']">TeamTime</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-accent transition-colors">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {getRoleLabel(user?.role)}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <aside className={`mobile-menu lg:hidden ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-4 border-b">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="TeamTime" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold font-['Manrope']">TeamTime</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t">
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-background/80 backdrop-blur border-b">
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="TeamTime" className="w-8 h-8 rounded-lg" />
            <span className="font-bold font-['Manrope']">TeamTime</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsDropdown />
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Desktop notification bar */}
        <div className="hidden lg:flex items-center justify-end px-8 py-3 border-b">
          <NotificationsDropdown />
        </div>

        {/* Page content */}
        <main className="p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
